/*
 * Deferred self-hosted video loader.
 *
 * Any <video data-deferred-video> is rendered with preload="none" and no
 * source (its variant URLs live in data-desktop-src / data-mobile-src). This
 * script starts observing at DOMContentLoaded and, once the element is near
 * the viewport, sets the src for ONLY the variant that matches the current
 * viewport (so phones never download the desktop file), then load() + play().
 * The actual src-set yields to idle time so it never races the LCP poster
 * fetch, but starts far earlier than window load.
 *
 * If the viewport later crosses the 750px breakpoint (rotation, resize),
 * already-activated videos swap to the matching variant.
 *
 * In the theme editor (Shopify.designMode) videos activate immediately so
 * previews are never blank, and re-render events are re-scanned.
 */
(function () {
  if (window.__b2oDeferredVideo) return;
  window.__b2oDeferredVideo = true;

  var observer = null;
  var desktopQuery = window.matchMedia('(min-width: 750px)');

  function pickSrc(video) {
    var desktopSrc = video.dataset.desktopSrc;
    var mobileSrc = video.dataset.mobileSrc;
    return desktopQuery.matches
      ? desktopSrc || mobileSrc
      : mobileSrc || desktopSrc;
  }

  function applySrc(video) {
    var src = pickSrc(video);
    if (!src || video.getAttribute('src') === src) return;

    video.setAttribute('src', src);
    video.load();

    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      // Autoplay can be rejected (e.g. reduced-data / power saving); ignore it.
      playPromise.catch(function () {});
    }
  }

  function activate(video, immediate) {
    if (!video || video.dataset.deferredLoaded === 'true') return;
    if (!pickSrc(video)) return;

    video.dataset.deferredLoaded = 'true';

    if (immediate) {
      applySrc(video);
    } else if ('requestIdleCallback' in window) {
      // Yield so the src-set + fetch never competes with LCP-critical work.
      window.requestIdleCallback(
        function () {
          applySrc(video);
        },
        { timeout: 1500 }
      );
    } else {
      window.setTimeout(function () {
        applySrc(video);
      }, 150);
    }
  }

  // Rotation / resize across the breakpoint: swap already-activated videos to
  // the variant that now matches. Not-yet-activated videos pick correctly on
  // first activation.
  function onBreakpointChange() {
    var videos = document.querySelectorAll(
      'video[data-deferred-video][data-deferred-loaded="true"]'
    );
    Array.prototype.forEach.call(videos, applySrc);
  }
  if (typeof desktopQuery.addEventListener === 'function') {
    desktopQuery.addEventListener('change', onBreakpointChange);
  } else if (typeof desktopQuery.addListener === 'function') {
    desktopQuery.addListener(onBreakpointChange);
  }

  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            activate(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '200px 0px' }
    );
    return observer;
  }

  var inDesignMode = window.Shopify && window.Shopify.designMode;

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var videos = scope.querySelectorAll('video[data-deferred-video]');
    if (!videos.length) return;

    var obs = inDesignMode ? null : getObserver();
    if (!obs) {
      // Theme editor (previews must never be blank) or no IntersectionObserver
      // support: activate immediately.
      Array.prototype.forEach.call(videos, function (video) {
        activate(video, true);
      });
      return;
    }

    Array.prototype.forEach.call(videos, function (video) {
      if (video.dataset.deferredLoaded !== 'true') obs.observe(video);
    });
  }

  function start() {
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  if (inDesignMode) {
    document.addEventListener('shopify:section:load', function (event) {
      scan(event.target);
    });
  }
})();
